import { TOTAL_WAVES } from '@rekindle/shared/questionnaire';
/**
 * Shared constants for the admin panel
 * Centralizes all duplicated enums, labels, and choice arrays
 */

// ============================================================
// PLAN TIERS
// ============================================================

/**
 * Plan tiers for MUI Select menus (value/label format)
 * Must match backend PlanTier enum: ember, flame, blaze
 */
export const PLAN_TIERS = [
  { value: 'ember', label: 'Ember (Free)' },
  { value: 'flame', label: 'Flame (Pro)' },
  { value: 'blaze', label: 'Blaze (Premium)' },
] as const;

/**
 * Short plan tier labels for chips and compact displays
 */
export const PLAN_TIER_LABELS: Record<string, string> = {
  ember: 'Ember',
  flame: 'Flame',
  blaze: 'Blaze',
};

/**
 * Full plan tier labels for detailed views
 */
export const PLAN_TIER_FULL_LABELS: Record<string, string> = {
  ember: 'Ember (Free)',
  flame: 'Flame (Pro)',
  blaze: 'Blaze (Premium)',
};

/**
 * Plan tier choices for react-admin SelectInput (id/name format)
 */
export const PLAN_TIER_CHOICES = [
  { id: 'ember', name: 'Ember (Free)' },
  { id: 'flame', name: 'Flame (Pro)' },
  { id: 'blaze', name: 'Blaze (Premium)' },
];

/**
 * MUI Chip color prop mapping for plan tiers
 */
export const PLAN_TIER_CHIP_COLORS: Record<string, 'default' | 'primary' | 'success'> = {
  ember: 'default',
  flame: 'primary',
  blaze: 'success',
};

// ============================================================
// USAGE FEATURES
// ============================================================

/**
 * Usage features for MUI Select menus (value/label format)
 * Must match backend BonusFeatureEnum (memory slots are retained capacity)
 */
export const USAGE_FEATURES = [
  { value: 'ai_chat_message', label: 'AI Chat Messages' },
  { value: 'profile_generation', label: 'Profile Generations' },
  { value: 'suggestion_generation', label: 'Suggestion Generations' },
  { value: 'image_analysis', label: 'Image Analysis' },
  { value: 'memory_photos', label: 'Memory photo slots' },
] as const;

/**
 * Usage feature choices for react-admin SelectInput (id/name format)
 */
export const USAGE_FEATURE_CHOICES = [
  { id: 'ai_chat_message', name: 'AI Chat Messages' },
  { id: 'profile_generation', name: 'Profile Generations' },
  { id: 'suggestion_generation', name: 'Suggestion Generations' },
  { id: 'image_analysis', name: 'Image Analysis' },
  { id: 'memory_photos', name: 'Memory photo slots' },
];

/**
 * Short usage feature labels for chips
 */
export const FEATURE_LABELS: Record<string, string> = {
  ai_chat_message: 'Chat',
  profile_generation: 'Profile',
  suggestion_generation: 'Suggestions',
  image_analysis: 'Image',
  memory_photos: 'Memory slots',
};

// ============================================================
// CONTENT: CARD TYPES
// ============================================================

/**
 * Card type choices for react-admin SelectInput
 * Based on Prisma CardType enum
 */
export const CARD_TYPE_CHOICES = [
  { id: 'Direct', name: 'Direct' },
  { id: 'Situational', name: 'Situational' },
  { id: 'Preference', name: 'Preference' },
  { id: 'Feeling', name: 'Feeling' },
  { id: 'Values', name: 'Values' },
];

/**
 * Card status choices for react-admin SelectInput
 */
export const CARD_STATUS_CHOICES = [
  { id: 'active', name: 'Active' },
  { id: 'inactive', name: 'Inactive' },
];

// ============================================================
// CONTENT: CATEGORIES
// ============================================================

/**
 * Layer choices for react-admin SelectInput
 * Based on Prisma Layer enum
 */
export const LAYER_CHOICES = [
  { id: 'Psychological_Assessment', name: 'Psychological Assessment' },
  { id: 'Happiness_Suggestions', name: 'Happiness Suggestions' },
];

// ============================================================
// CONTENT: IMPORTANT DATES
// ============================================================

/**
 * Date type choices for react-admin SelectInput
 */
export const DATE_TYPE_CHOICES = [
  { id: 'birthday', name: 'Birthday' },
  { id: 'anniversary', name: 'Anniversary' },
  { id: 'custom', name: 'Custom' },
  { id: 'holiday', name: 'Holiday' },
  { id: 'reminder', name: 'Reminder' },
];

// ============================================================
// CONTENT: QUESTIONS
// ============================================================

/**
 * Question tier choices for react-admin SelectInput
 */
export const QUESTION_TIER_CHOICES = [
  { id: 1, name: 'Tier 1 (Onboarding)' },
  { id: 2, name: 'Tier 2 (Later)' },
];

/**
 * Question askAt choices for react-admin SelectInput
 */
export const QUESTION_ASK_AT_CHOICES = [
  { id: 'onboarding', name: 'Onboarding' },
  ...Array.from({ length: TOTAL_WAVES }, (_, index) => ({ id: `wave_${index + 1}`, name: `Set ${index + 1}` })),
];

// ============================================================
// LANGUAGES (for translations)
// ============================================================

import { SUPPORTED_LANGUAGES as SHARED_LANGUAGES } from '@rekindle/shared';

/**
 * Language display names for admin UI
 */
const LANGUAGE_NAMES: Record<string, string> = {
  en: 'English',
  fr: 'French',
  es: 'Spanish',
  de: 'German',
  it: 'Italian',
};

/**
 * Supported languages for translations (derived from shared constants)
 */
export const SUPPORTED_LANGUAGES = SHARED_LANGUAGES.map(code => ({
  id: code,
  name: LANGUAGE_NAMES[code] || code,
}));

/**
 * Language labels for display (derived from shared constants)
 */
export const LANGUAGE_LABELS: Record<string, string> = Object.fromEntries(
  SHARED_LANGUAGES.map(code => [code, LANGUAGE_NAMES[code] || code])
);
